import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { USER_GROUP_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-registry-select.dto';
import { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { USER_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/user/user-registry-select.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { MultiSelectModule } from 'primeng/multiselect';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { fetchAllByIncludeIds } from '@zaoblako/web/shared/helpers/include-ids-pagination.helper';
import { forkJoin, map, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

type UserListItem = UserFindInterface & { name: string; };
type GroupListItem = UserGroupFindInterface;

@Component({
  standalone: true,
  selector: 'crm-user-and-group-multi-select',
  templateUrl: './user-and-group-multi-select.component.html',
  imports: [CommonModule, MultiSelectModule, FormsModule, MultiSelectComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAndGroupMultiSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @ViewChild('multiSelectComponent') public multiSelectComponent: MultiSelectComponent;

  @Input() public withBots = false;
  @Input() public customRequest = false;

  @Input() public users: UserListItem[] = [];
  @Input() public userGroups: GroupListItem[] = [];

  @Input() public selectedEntities: Array<{ user?: UserFindInterface; group?: UserGroupFindInterface }> = [];
  @Output() public selectedEntitiesChange = new EventEmitter<Array<{ user?: UserFindInterface; group?: UserGroupFindInterface }>>();

  @Input() public disabled = false;
  @Input() public rememberSelected = false;
  @Input() public styleClass = '';
  @Input() public class = '';
  @Input() public itemStyleClass: string;
  @Input() public appendTo: any;

  public auditors: { label: string; items: (UserListItem | GroupListItem)[] }[] = [];
  public _selected: (UserListItem | GroupListItem)[] = [];

  public queryParams: DropdownBaseQueryInterface = { limit: 20, offset: 0 };
  public loading = false;
  public paginationLoading = false;

  private usersPage: UserListItem[] = [];
  private groupsPage: GroupListItem[] = [];
  private usersCount = 0;
  private groupsCount = 0;
  private usersOffset = DEFAULT_OFFSET;
  private groupsOffset = DEFAULT_OFFSET;

  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly globalService = inject(GlobalService);
  private readonly userService = inject(UserService);
  private readonly userGroupService = inject(UserGroupService);

  private originalSelected: Array<{ user?: UserFindInterface; group?: UserGroupFindInterface }> = [];
  private firstLoadDone$ = new ReplaySubject<void>(1);
  private loadSub?: Subscription;
  private searchSub?: Subscription;
  private readonly searchInput$ = new Subject<string>();
  private searchDebounceSub?: Subscription;
  private initialPageSnapshot: {
    usersPage: UserListItem[];
    groupsPage: GroupListItem[];
    usersCount: number;
    groupsCount: number;
  } | null = null;

  private pendingUserIds: number[] = [];
  private pendingGroupIds: number[] = [];
  private initialLoadCompleted = false;

  @Input() public set setSelectedUserIds(ids: number[]) {
    this.pendingUserIds = ids ?? [];
    if (this.initialLoadCompleted) {
      this.applyPendingSelections();
    }
  }

  @Input() public set setSelectedUserGroupIds(ids: number[]) {
    this.pendingGroupIds = ids ?? [];
    if (this.initialLoadCompleted) {
      this.applyPendingSelections();
    }
  }

  public ngOnInit(): void {
    if (this.customRequest) {
      if (this.users?.length || this.userGroups?.length) {
        this.syncAuditorsFromInputs();
      }
      this.hydrateFromSelectedEntities();
      this.setSelected();
      this.firstLoadDone$.next();
      return;
    }
    this.firstLoad();
    this.searchDebounceSub = this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((name) => this.executeSearch(name));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.customRequest && (changes.users || changes.userGroups) && (this.users?.length || this.userGroups?.length)) {
      this.syncAuditorsFromInputs();
      this.cdRef.markForCheck();
    }
    if (changes.selectedEntities) {
      this.hydrateFromSelectedEntities();
      this.setSelected();
    }
    if (changes.withBots && !changes.withBots.firstChange && !this.customRequest) {
      this.invalidateInitialPageSnapshot();
      this.firstLoad();
    }
  }

  public ngOnDestroy(): void {
    this.unbindScrollPagination();
    this.loadSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.searchDebounceSub?.unsubscribe();
    this.searchInput$.complete();
    this.firstLoadDone$.complete();
  }

  public setSelected(): void {
    this.originalSelected = this.selectedEntities
      ? ([...this.selectedEntities] as { user?: UserFindInterface; group?: UserGroupFindInterface; }[])
      : [];
    this._selected = this.selectedEntities
      ? this.selectedEntities.map((auditor) =>
          this.toListItem((auditor.user ?? auditor.group) as UserFindInterface | UserGroupFindInterface)
        )
      : [];
    this.cdRef.markForCheck();
  }

  public onSelectedChange(items: (UserListItem | GroupListItem)[]): void {
    this._selected = items ?? [];
    const auditors = items.map((item) => {
      const plainItem = this.isUserItem(item) ? { user: item } : { group: item };
      if (this.rememberSelected) {
        const currentAuditor = this.originalSelected.find((auditor) =>
          auditor.user ? auditor.user.id === item.id : auditor.group?.id === item.id
        );
        return currentAuditor || plainItem;
      }
      return plainItem;
    });
    this.selectedEntities = auditors;
    this.selectedEntitiesChange.emit(auditors);
  }

  public onShowPanel(): void {
    if (this.customRequest) {
      return;
    }

    setTimeout(() => this.bindScrollPagination(), 100);
  }

  public onHidePanel(): void {
    this.unbindScrollPagination();
  }

  private scrollElement: HTMLElement | null = null;

  private readonly onScrollPagination = (): void => {
    const el = this.scrollElement;
    if (!el) {
      return;
    }

    if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight - 1) {
      this.loadMore();
    }
  };

  private bindScrollPagination(): void {
    const overlay = this.multiSelectComponent?.multiSelect?.overlayViewChild?.overlayViewChild
      ?.nativeElement as HTMLElement | undefined;
    const el = (overlay?.querySelector('.p-multiselect-items-wrapper') as HTMLElement | null) ?? null;
    if (!el || this.scrollElement === el) {
      return;
    }

    this.unbindScrollPagination();
    this.scrollElement = el;
    el.addEventListener('scroll', this.onScrollPagination, { passive: true });
  }

  private unbindScrollPagination(): void {
    if (this.scrollElement) {
      this.scrollElement.removeEventListener('scroll', this.onScrollPagination);
      this.scrollElement = null;
    }
  }

  private loadMore(): void {
    if (this.customRequest || !this.hasMorePages() || this.loading || this.paginationLoading) {
      return;
    }

    this.paginationLoading = true;
    this.cdRef.markForCheck();
    this.loadPage(true);
  }

  private hasMorePages(): boolean {
    return this.usersPage.length < this.usersCount || this.groupsPage.length < this.groupsCount;
  }

  public search(name: string): void {
    if (this.customRequest) {
      return;
    }
    if (!name?.length) {
      this.executeSearch('');
      return;
    }
    this.searchInput$.next(name);
  }

  private executeSearch(name: string): void {
    if (!name?.length) {
      this.reloadInitialPage();
      return;
    }

    this.searchSub?.unsubscribe();
    this.loading = true;
    this.queryParams.search = name;
    this.queryParams.offset = 0;
    this.cdRef.markForCheck();
    this.searchSub = this.fetchPage(this.queryParams, false).subscribe({
      next: ({ users, groups, usersCount, groupsCount }) => {
        this.usersPage = users;
        this.groupsPage = groups;
        this.usersCount = usersCount;
        this.groupsCount = groupsCount;
        this.rebuildAuditors();
        this.loading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  private firstLoad(): void {
    if (!this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled) {
      this.firstLoadDone$.next();
      return;
    }
    this.reloadInitialPage();
  }

  private reloadInitialPage(): void {
    if (this.initialPageSnapshot) {
      this.restoreFromInitialPageSnapshot();
      return;
    }

    this.searchSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.queryParams.search = undefined;
    this.queryParams.offset = 0;
    this.loading = true;
    this.paginationLoading = false;
    this.usersPage = [];
    this.groupsPage = [];
    this.usersCount = 0;
    this.groupsCount = 0;
    this.usersOffset = DEFAULT_OFFSET;
    this.groupsOffset = DEFAULT_OFFSET;
    this.rebuildAuditors();
    this.cdRef.markForCheck();
    this.loadPage(false);
  }

  private restoreFromInitialPageSnapshot(): void {
    if (!this.initialPageSnapshot) {
      return;
    }

    this.searchSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.queryParams.search = undefined;
    this.queryParams.offset = 0;
    this.usersPage = [...this.initialPageSnapshot.usersPage];
    this.groupsPage = [...this.initialPageSnapshot.groupsPage];
    this.usersCount = this.initialPageSnapshot.usersCount;
    this.groupsCount = this.initialPageSnapshot.groupsCount;
    this.rebuildAuditors();
    this.cdRef.markForCheck();
  }

  private saveInitialPageSnapshot(): void {
    this.initialPageSnapshot = {
      usersPage: [...this.usersPage],
      groupsPage: [...this.groupsPage],
      usersCount: this.usersCount,
      groupsCount: this.groupsCount
    };
  }

  private invalidateInitialPageSnapshot(): void {
    this.initialPageSnapshot = null;
  }

  private resetPages(): void {
    this.usersPage = [];
    this.groupsPage = [];
    this.usersCount = 0;
    this.groupsCount = 0;
  }

  private loadPage(append: boolean): void {
    this.loadSub?.unsubscribe();
    this.loadSub = this.fetchPage(this.queryParams, append).subscribe({
      next: ({ users, groups, usersCount, groupsCount }) => {
        this.usersPage = append
          ? this.usersPage.concat(users.filter((user) => !this.usersPage.some((item) => item.id === user.id)))
          : users;
        this.groupsPage = append
          ? this.groupsPage.concat(groups.filter((group) => !this.groupsPage.some((item) => item.id === group.id)))
          : groups;
        this.usersCount = usersCount;
        this.groupsCount = groupsCount;
        this.rebuildAuditors();
        if (!append && !this.queryParams.search) {
          this.saveInitialPageSnapshot();
        }
        this.loading = false;
        this.paginationLoading = false;
        this.cdRef.markForCheck();
        if (!this.initialLoadCompleted) {
          this.applyPendingSelections();
          this.initialLoadCompleted = true;
          this.firstLoadDone$.next();
        }
      },
      error: () => {
        this.loading = false;
        this.paginationLoading = false;
        this.cdRef.markForCheck();
        if (!this.initialLoadCompleted) {
          this.initialLoadCompleted = true;
          this.firstLoadDone$.next();
        }
      }
    });
  }

  private fetchPage(
    queryParams: Partial<DropdownBaseQueryInterface>,
    append: boolean
  ): Observable<{
    users: UserListItem[];
    groups: GroupListItem[];
    usersCount: number;
    groupsCount: number;
  }> {
    const limit = queryParams.limit ?? DEFAULT_LIMIT;
    const search = queryParams.search;
    const fetchUsers = !append || this.usersPage.length < this.usersCount;
    const fetchGroups = !append || this.groupsPage.length < this.groupsCount;

    let usersOffset = DEFAULT_OFFSET;
    let groupsOffset = DEFAULT_OFFSET;
    if (append) {
      if (fetchUsers) {
        this.usersOffset += limit;
        usersOffset = this.usersOffset;
      } else {
        usersOffset = this.usersOffset;
      }
      if (fetchGroups) {
        this.groupsOffset += limit;
        groupsOffset = this.groupsOffset;
      } else {
        groupsOffset = this.groupsOffset;
      }
    } else {
      this.usersOffset = DEFAULT_OFFSET;
      this.groupsOffset = DEFAULT_OFFSET;
    }

    const users$ = fetchUsers
      ? this.userService.findRegistryItems({
          limit,
          offset: usersOffset,
          search,
          select: [...USER_WIDGET_REGISTRY_SELECT],
          withBots: this.withBots
        })
      : of({ items: [], count: this.usersCount });

    const groups$ = fetchGroups
      ? this.userGroupService.findRegistryPage({
          limit,
          offset: groupsOffset,
          search,
          select: [...USER_GROUP_WIDGET_REGISTRY_SELECT]
        })
      : of({ items: [], count: this.groupsCount });

    return forkJoin([users$, groups$]).pipe(
      map(([userPage, groupPage]) => ({
        users: userPage.items.map((user) => this.normalizeUser(user)),
        groups: groupPage.items,
        usersCount: userPage.count,
        groupsCount: groupPage.count
      }))
    );
  }

  private applyPendingSelections(): void {
    if (this.pendingUserIds.length) {
      this.hydrateUsers(this.pendingUserIds);
    }
    if (this.pendingGroupIds.length) {
      this.hydrateGroups(this.pendingGroupIds);
    }
  }

  private hydrateUsers(ids: number[]): void {
    if (!ids?.length) {
      return;
    }

    const missingIds = ids.filter(
      (id) =>
        !this.usersPage.some((user) => user.id === id) &&
        !this._selected.some((item) => this.isUserItem(item) && item.id === id)
    );
    if (!missingIds.length) {
      this.applySelectedUserIds(ids);
      return;
    }
    fetchAllByIncludeIds(missingIds, (chunk) =>
      this.userService.findRegistryItems({
        includeIds: chunk,
        limit: chunk.length,
        offset: 0,
        select: [...USER_WIDGET_REGISTRY_SELECT],
        withBots: this.withBots,
        withBanned: true,
        withDeleted: true
      })
    ).subscribe({
      next: (items) => {
        const normalized = items.map((user) => this.normalizeUser(user));
        this.usersPage = this.usersPage.concat(
          normalized.filter((user) => !this.usersPage.some((item) => item.id === user.id))
        );
        this.rebuildAuditors();
        this.applySelectedUserIds(ids);
        this.cdRef.markForCheck();
      }
    });
  }

  private hydrateGroups(ids: number[]): void {
    if (!ids?.length) {
      return;
    }

    const missingIds = ids.filter(
      (id) =>
        !this.groupsPage.some((group) => group.id === id) &&
        !this._selected.some((item) => !this.isUserItem(item) && item.id === id)
    );
    if (!missingIds.length) {
      this.applySelectedGroupIds(ids);
      return;
    }
    fetchAllByIncludeIds(missingIds, (chunk) =>
      this.userGroupService.findRegistryPage({
        includeIds: chunk,
        limit: chunk.length,
        offset: 0,
        select: [...USER_GROUP_WIDGET_REGISTRY_SELECT]
      })
    ).subscribe({
      next: (items) => {
        this.groupsPage = this.groupsPage.concat(
          items.filter((group) => !this.groupsPage.some((item) => item.id === group.id))
        );
        this.rebuildAuditors();
        this.applySelectedGroupIds(ids);
        this.cdRef.markForCheck();
      }
    });
  }

  private applySelectedUserIds(ids: number[]): void {
    const users = this.usersPage.filter((user) => ids.includes(user.id));
    this._selected = [
      ...this._selected.filter((item) => !this.isUserItem(item) || ids.includes(item.id)),
      ...users.filter((user) => !this._selected.some((item) => this.isUserItem(item) && item.id === user.id))
    ];
    this.setSelected();
  }

  private applySelectedGroupIds(ids: number[]): void {
    const groups = this.groupsPage.filter((group) => ids.includes(group.id));
    this._selected = [
      ...this._selected.filter((item) => this.isUserItem(item) || ids.includes(item.id)),
      ...groups.filter((group) => !this._selected.some((item) => !this.isUserItem(item) && item.id === group.id))
    ];
    this.setSelected();
  }

  private syncAuditorsFromInputs(): void {
    this.usersPage = (this.users ?? []).map((user) => this.normalizeUser(user));
    this.groupsPage = [...(this.userGroups ?? [])];
    this.rebuildAuditors();
  }

  private rebuildAuditors(): void {
    const sortByName = (a: { name?: string }, b: { name?: string }) => (a.name ?? '').localeCompare(b.name ?? '');
    const users = this.customRequest ? [...this.usersPage].sort(sortByName) : [...this.usersPage];
    const groups = this.customRequest ? [...this.groupsPage].sort(sortByName) : [...this.groupsPage];

    for (const item of this._selected ?? []) {
      if (this.isUserItem(item)) {
        if (!users.some((user) => user.id === item.id)) {
          users.push(item);
        }
      } else if (!groups.some((group) => group.id === item.id)) {
        groups.push(item);
      }
    }

    this.auditors = [
      { label: 'Пользователи', items: users },
      { label: 'Группы', items: groups }
    ];
    this.multiSelectComponent?.syncOptions(this.auditors);
  }

  private normalizeUser(user: UserFindInterface): UserListItem {
    return {
      ...user,
      name: user.profile?.name ?? user.job ?? ''
    };
  }

  private toListItem(entity?: UserFindInterface | UserGroupFindInterface): UserListItem | GroupListItem {
    if (!entity) {
      return entity as GroupListItem;
    }
    return this.isUserEntity(entity) ? this.normalizeUser(entity) : entity;
  }

  private isUserEntity(entity: UserFindInterface | UserGroupFindInterface): entity is UserFindInterface {
    return 'profile' in entity;
  }

  private isUserItem(item: UserListItem | GroupListItem): item is UserListItem {
    return 'profile' in item;
  }

  private findUserItem(id: number): UserListItem | undefined {
    return this.usersPage.find((user) => user.id === id);
  }

  private findGroupItem(id: number): GroupListItem | undefined {
    return this.groupsPage.find((group) => group.id === id);
  }

  private hydrateFromSelectedEntities(): void {
    if (!this.selectedEntities?.length) {
      return;
    }

    const userIds = this.selectedEntities
      .filter((entity) => entity.user?.id)
      .map((entity) => entity.user!.id);
    const groupIds = this.selectedEntities
      .filter((entity) => entity.group?.id)
      .map((entity) => entity.group!.id);

    if (userIds.length) {
      this.hydrateUsers(userIds);
    }
    if (groupIds.length) {
      this.hydrateGroups(groupIds);
    }
  }
}
